import {Handle} from "@luciad/ria/util/Evented";
import {Controller} from "@luciad/ria/view/controller/Controller";
import {FlickerController} from "@luciad/ria/view/controller/FlickerController";
import {SwipeController} from "@luciad/ria/view/controller/SwipeController";
import {Layer} from "@luciad/ria/view/Layer";
import {LayerGroup} from "@luciad/ria/view/LayerGroup";
import {LayerTreeNode} from "@luciad/ria/view/LayerTreeNode";
import {LayerTreeVisitor} from "@luciad/ria/view/LayerTreeVisitor";
import {Map} from "@luciad/ria/view/Map";
import React, {ReactNode, useEffect, useState} from "react";
import {CompositeController} from "@luciad/ria/view/controller/CompositeController";
import "./LayerPicker.css";
import {LayerSelectionPanel} from "./LayerSelectionPanel";

function getOriginalMapController(map: Map): Controller | null {
  const controller = map.controller;
  if (controller instanceof CompositeController && controller.delegates[1] &&
      (controller.delegates[0] instanceof FlickerController || controller.delegates[0] instanceof SwipeController)) {
    return controller.delegates[1];
  } else {
    return null;
  }
}

interface Props {
  map: Map;
  controller: SwipeController | FlickerController;
  leftContainerClass?: string;
  centralContainerChild?: ReactNode;
  rightContainerClass?: string;
  isVertical?: boolean;
  centerPos?: number
}

export const LayerPicker = ({
                              map,
                              controller,
                              leftContainerClass = "",
                              centralContainerChild,
                              rightContainerClass = "",
                              isVertical = true,
                              centerPos = map.viewSize[0] / 2
                            }: Props) => {
  const [isEditing, setEditing] = useState(true);
  const [allVisibleLayers, setAllVisibleLayers] = useState<Layer[]>([]);
  const [leftLayers, setLeftLayers] = useState<Layer[]>([]);
  const [rightLayers, setRightLayers] = useState<Layer[]>([]);

  useEffect(() => {
    let layerVisibleListeners = [] as Handle[];

    function handleLayersChange(): void {
      for (const visibleListener of layerVisibleListeners) {
        visibleListener.remove();
      }
      layerVisibleListeners = [];

      const layers = controller.layers;
      let leftControllerLayers: Layer[] = [];
      let rightControllerLayers: Layer[] = [];
      if (layers.length === 2) {
        leftControllerLayers = layers[0];
        rightControllerLayers = layers[1];
      }
      const allLayersTopDown: Layer[] = [];
      const leftLayersTopDown: Layer[] = [];
      const rightLayersTopDown: Layer[] = [];
      const layerTreeVisitor: LayerTreeVisitor = {
        visitLayer: (layer: Layer): LayerTreeVisitor.ReturnValue => {
          allLayersTopDown.push(layer);
          if (leftControllerLayers.indexOf(layer) >= 0) {
            leftLayersTopDown.push(layer);
          }
          if (rightControllerLayers.indexOf(layer) >= 0) {
            rightLayersTopDown.push(layer);
          }
          return LayerTreeVisitor.ReturnValue.CONTINUE;
        },
        visitLayerGroup(layerGroup: LayerGroup): LayerTreeVisitor.ReturnValue {
          layerGroup.visitChildren(layerTreeVisitor, LayerTreeNode.VisitOrder.TOP_DOWN);
          return LayerTreeVisitor.ReturnValue.CONTINUE;
        }
      };
      map.layerTree.visitChildren(layerTreeVisitor, LayerTreeNode.VisitOrder.TOP_DOWN);

      for (const layer of allLayersTopDown) {
        const visibleChangedListener = layer.on("VisibilityChanged", () => {
          setAllVisibleLayers(allLayersTopDown.filter(layer => layer.visible));
        });
        layerVisibleListeners.push(visibleChangedListener);
      }
      setAllVisibleLayers(allLayersTopDown.filter(layer => layer.visible));
      setLeftLayers(leftLayersTopDown)
      setRightLayers(rightLayersTopDown);
    }

    const layerAddedListener = map.layerTree.on("NodeAdded", handleLayersChange);
    const layerMovedListener = map.layerTree.on("NodeMoved", handleLayersChange);
    const layerRemovedListener = map.layerTree.on("NodeRemoved", handleLayersChange);
    const layersChangeListener = controller.on("LayersChange", handleLayersChange);

    handleLayersChange();
    return () => {
      for (const listener of layerVisibleListeners) {
        listener.remove();
      }
      layerAddedListener.remove();
      layerMovedListener.remove();
      layerRemovedListener.remove();
      layersChangeListener.remove();
    }
  }, [map, controller]);

  function handleLayerSelectionChange(layer: Layer, selected: boolean, layersOfSide: Layer[],
                                      otherSideLayers: Layer[]): [Layer[], Layer[]] {
    let newLayersOfSide: Layer[];
    let newLayersOfOtherSide: Layer[];
    if (selected) {
      newLayersOfSide = [...layersOfSide, layer];
      newLayersOfOtherSide = otherSideLayers.filter(otherSideLayer => otherSideLayer.id !== layer.id);
    } else {
      newLayersOfSide = layersOfSide.filter(layerOfSide => layerOfSide.id !== layer.id);
      newLayersOfOtherSide = otherSideLayers;
    }
    return [newLayersOfSide, newLayersOfOtherSide];
  }

  function handleLeftSelectionChange(layer: Layer, selected: boolean) {
    const [newLeftLayers, newRightLayers] = handleLayerSelectionChange(layer, selected, leftLayers, rightLayers);
    controller.layers = [newLeftLayers, newRightLayers];
    setLeftLayers(newLeftLayers);
    setRightLayers(newRightLayers);
  }

  function handleRightSelectionChange(layer: Layer, selected: boolean) {
    const [newRightLayers, newLeftLayers] = handleLayerSelectionChange(layer, selected, rightLayers, leftLayers);
    controller.layers = [newLeftLayers, newRightLayers];
    setLeftLayers(newLeftLayers);
    setRightLayers(newRightLayers);
  }

  function handleStop() {
    map.controller = getOriginalMapController(map);
  }

  const transformLeft = isVertical ? `translate(${centerPos}px, 0) translateX(-100%)`
                                   : `translate(0, ${centerPos}px) translateY(-100%)`;
  const transformCenter = isVertical ? `translate(${centerPos}px, 0) translateX(-50%)`
                                     : `translate(0, ${centerPos}px) translateY(-50%)`;
  const transformRight = isVertical ? `translate(${centerPos}px, 0)` : `translate(0, ${centerPos}px)`;

  return (
      <div className="layer-picker-container">
        <div
            className={
              "layer-picker layer-picker-top-left-container " +
              (isVertical ? "layer-picker-vertical " : "layer-picker-horizontal ") +
              leftContainerClass
            }
            style={{transform: transformLeft}}
        >
          <LayerSelectionPanel editable={isEditing} layers={allVisibleLayers} selectedLayers={leftLayers}
                               onSelectionChange={handleLeftSelectionChange}/>

        </div>
        <div
            className={"layer-picker layer-picker-center-container " +
                       (isVertical ? "layer-picker-vertical " : "layer-picker-horizontal ")}
            style={{transform: transformCenter}}
        >
          <button title="Stop" onClick={handleStop}>✕</button>
          {isEditing ? <button title="Confirm selection" onClick={() => setEditing(false)}> ✓ </button>
                     : <button title="Edit selection" onClick={() => setEditing(true)}> 🖉 </button>}
          {centralContainerChild}
        </div>
        <div
            className={"layer-picker layer-picker-bottom-right-container " +
                       (isVertical ? "layer-picker-vertical " : "layer-picker-horizontal ") +
                       rightContainerClass}
            style={{transform: transformRight}}
        >
          <LayerSelectionPanel editable={isEditing} layers={allVisibleLayers} selectedLayers={rightLayers}
                               onSelectionChange={handleRightSelectionChange} textLeft={!isVertical}/>
        </div>
      </div>
  );
};
